import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Image from '@atlaskit/image';
import { AtlassianIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import Skeleton from '@atlaskit/skeleton';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { type AgentKnowledgeConfiguration } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	useConnectorsIsFetching,
	useEnabledKnowledgeSources,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import { useKnowledgeConnectors } from '../../../controllers/knowledge-connector/index.ts';
import { KnowledgeFilterServiceImpl } from '../../../services/knowledge-filter/index.ts';
import { InlineIconText } from '../inline-icon-text/index.tsx';

import messages from './messages.ts';

export const AI_MATE_EXPERIENCE_ID_HEADER = 'ai-mate';
export const ROVO_PRODUCT_HEADER = 'rovo';
export const ATLASSIAN_STUDIO_EXPERIENCE_ID_HEADER = 'atlassian-agent-studio';

type KnowledgeSourcePillProps = {
	ari?: string;
	cloudId: string;
	source: string;
	/**
	 * When true, always resolve friendlyName / iconUrl from `useKnowledgeConnectors` (API) instead of
	 * the connectors store. Use in Agent Studio (and similar surfaces) where chat may not be mounted
	 * so the store is not populated.
	 */
	preferDirectConnectorFetch?: boolean;
	/**
	 * Used to filter `useKnowledgeConnectors` results to only include whitelisted connectors on the BE
	 */
	agentCreatorType?: AgentCreatorType;
};

export const KnowledgeSourcePill = ({
	ari,
	source,
	cloudId,
	preferDirectConnectorFetch = false,
	agentCreatorType,
}: KnowledgeSourcePillProps): React.JSX.Element | null => {
	di();
	const { formatMessage } = useIntl();

	const useConnectorData = preferDirectConnectorFetch;
	const [storedSources] = useEnabledKnowledgeSources();
	const [storedIsFetching] = useConnectorsIsFetching();

	const assistanceServiceParams = {
		product: ROVO_PRODUCT_HEADER,
		touchpointSource: 'atlassian-agent-studio-rovo-knowledge-card-knowledge-list',
		experienceId: AI_MATE_EXPERIENCE_ID_HEADER,
		siteId: cloudId,
		headers: { 'X-Channel-Id': ATLASSIAN_STUDIO_EXPERIENCE_ID_HEADER },
	};
	const { enabledKnowledgeSources, isFetching } = useKnowledgeConnectors({
		agentCreatorType,
		assistanceServiceParams,
		enabled: useConnectorData,
	});

	const sources = useConnectorData ? enabledKnowledgeSources : storedSources;
	const fetching = useConnectorData ? isFetching : storedIsFetching;

	const isHelpCenterAri = ari?.includes('help-center') ?? false;

	const { data: entity, isFetching: isFetchingEntity } = useQuery({
		queryKey: ['knowledge-source-pill', ari ?? ''],
		queryFn: async () => {
			if (!ari) {
				return;
			}

			const service = new KnowledgeFilterServiceImpl(cloudId);

			const entities = await service.getEntitiesByAris({ aris: [ari] });

			if (entities.length > 0) {
				return entities[0];
			}
		},
		enabled: !!ari,
	});

	if (fetching || isFetchingEntity) {
		return <Skeleton isShimmering width={100} height={16} />;
	}

	if (!ari) {
		const sourceDetails = sources.find((s: AgentKnowledgeConfiguration) => s.source === source);
		const sourceName = sourceDetails?.friendlyName ?? source;
		const iconUrl = sourceDetails?.iconUrl;

		const content = (() => {
			if (source === 'jira' || source === 'jsm') {
				return formatMessage(messages.allSpaces);
			}
			if (source === 'confluence') {
				return formatMessage(messages.allContent);
			}
			return sourceName;
		})();

		return (
			<InlineIconText
				icon={
					iconUrl ? (
						<Image
							src={sourceDetails?.iconUrl}
							width={16}
							height={16}
							alt={formatMessage(messages.knowledgeSourcePill, {
								knowledgeSource: sourceName,
							})}
						/>
					) : (
						<AtlassianIcon size="xsmall" appearance="brand" />
					)
				}
				content={content}
				fullWidth
			/>
		);
	}

	if (!entity) {
		return null;
	}

	// Help center: SmartCard does not support it; show name from GQL (KnowledgeFilterService) + JSM icon
	if (isHelpCenterAri && fg('agent_studio_jsm_help_center_knowledge')) {
		const jsmSource = sources.find((s: AgentKnowledgeConfiguration) => s.source === 'jsm');
		const iconUrl = jsmSource?.iconUrl;
		return (
			<InlineIconText
				icon={
					iconUrl ? (
						<Image
							src={iconUrl}
							width={16}
							height={16}
							alt={formatMessage(messages.knowledgeSourcePill, {
								knowledgeSource: entity.displayName,
							})}
						/>
					) : (
						<AtlassianIcon size="xsmall" appearance="brand" />
					)
				}
				content={entity.displayName}
				fullWidth
			/>
		);
	}

	return (
		<SmartCard
			url={entity.url}
			appearance="inline"
			showHoverPreview
			onClick={(event) => {
				event.preventDefault();
				window.open(entity?.url, '_blank', 'noopener, noreferrer');
			}}
		/>
	);
};
